import { CircularProgress, MenuItem, TextField } from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { API_KEY_USE_CASES, type ApiKeyUseCaseId } from './constants';

type UseCaseConfig = (typeof API_KEY_USE_CASES)[number];

interface CreateApiKeyUseCaseSelectProps {
  value: ApiKeyUseCaseId;
  onChange: (value: ApiKeyUseCaseId) => void;
  availableUseCases: UseCaseConfig[];
  config: UseCaseConfig | undefined;
  hasCodeAgent: boolean;
  resourcesLoading: boolean;
}

export const CreateApiKeyUseCaseSelect: React.FC<CreateApiKeyUseCaseSelectProps> = ({
  value,
  onChange,
  availableUseCases,
  config,
  hasCodeAgent,
  resourcesLoading,
}) => {
  const { t } = useTranslation();

  return (
    <div>
      <p className="mb-2 text-sm font-semibold text-gray-800 dark:text-slate-200">
        {t('apiKeys.modal.useCase', { defaultValue: 'Use case' })}{' '}
        <span className="text-red-500">*</span>
      </p>
      <p className="mb-2 text-xs leading-5 text-gray-500 dark:text-slate-400">
        {t('apiKeys.modal.useCaseHelper', {
          defaultValue:
            'Choose a preset flow. Selected scopes is best for personal service-level API keys.',
        })}
      </p>

      {resourcesLoading ? (
        <div className="flex items-center gap-2 py-4">
          <CircularProgress size={18} />
          <span className="text-sm text-gray-500 dark:text-slate-400">
            {t('apiKeys.modal.loadingResources', { defaultValue: 'Loading resources...' })}
          </span>
        </div>
      ) : (
        <TextField
          select
          fullWidth
          value={value}
          onChange={(event) => onChange(event.target.value as ApiKeyUseCaseId)}
          size="small"
          SelectProps={{
            renderValue: (value) => {
              const useCase = availableUseCases.find((item) => item.id === value);
              return useCase
                ? t(`apiKeys.modal.presets.${useCase.id}`, {
                    defaultValue: useCase.title,
                  })
                : '';
            },
          }}
          helperText={
            config
              ? t(config.descriptionKey, {
                  defaultValue: config.description,
                })
              : undefined
          }
        >
          {availableUseCases.map((useCase) => (
            <MenuItem key={useCase.id} value={useCase.id}>
              <div className="min-w-0 py-1">
                <p className="text-sm font-medium text-gray-800 dark:text-slate-100">
                  {t(`apiKeys.modal.presets.${useCase.id}`, {
                    defaultValue: useCase.title,
                  })}
                </p>
                <p className="mt-0.5 whitespace-normal text-xs leading-4 text-gray-500 dark:text-slate-400">
                  {t(useCase.descriptionKey, {
                    defaultValue: useCase.description,
                  })}
                </p>
              </div>
            </MenuItem>
          ))}
        </TextField>
      )}
      {!hasCodeAgent && !resourcesLoading && (
        <p className="mt-2 text-xs text-amber-600 dark:text-amber-400">
          {t('apiKeys.modal.cliLoginRequiresPackage', {
            defaultValue:
              'AppAI CLI login requires a package with Coding Agent enabled. Upgrade your package to generate CLI login keys.',
          })}
        </p>
      )}
    </div>
  );
};
